import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import { assert } from '@chainflip/utils/assertion';
import { CHAINFLIP_SS58_PREFIX } from '@chainflip/utils/consts';
import * as ss58 from '@chainflip/utils/ss58';
import { initPolkadotSnap, isPolkadotSnapInstalled } from '@chainsafe/metamask-polkadot-adapter';
import type { Signer as InjectedSigner } from '@polkadot/api/types';
import { useQuery } from '@tanstack/react-query';
import { useDisconnect as useDisconnectEvm } from 'wagmi';
import useIsEvmAccountInitialized from '@/shared/hooks/useIsEvmAccountInitialized';

export type ChainflipAccountId = `cF${string}`;

const reencodeSS58 = (address: string): ChainflipAccountId => {
  const decoded = ss58.decode(address);
  return ss58.encode({ ...decoded, ss58Format: CHAINFLIP_SS58_PREFIX }) as ChainflipAccountId;
};

export interface InjectedExtension {
  name: string;
  version: string;
}

interface RawInjectedAccount {
  address: string;
  name?: string;
  type?: string;
}

interface RawInjected {
  accounts: { get: (anyType?: boolean) => Promise<RawInjectedAccount[]> };
  signer: InjectedSigner;
}

type EnabledExtension = RawInjected & InjectedExtension;

interface InjectedWindowProvider {
  connect?: (origin: string) => Promise<EnabledExtension>;
  enable?: (origin: string) => Promise<RawInjected>;
  version?: string;
}

interface BaseInjectedAccountWithMeta {
  idHex: `0x${string}`;
  idSs58: ChainflipAccountId;
  readonly?: false;
  meta: {
    name?: string;
    source: string;
  };
  type: string;
}

interface SubstrateInjectedAccountWithMeta extends BaseInjectedAccountWithMeta {
  type: 'substrate';
}

interface EvmInjectedAccountWithMeta extends BaseInjectedAccountWithMeta {
  type: 'evm';
  evmAddress: `0x${string}`;
  onboarded: boolean;
}

export type InjectedAccountWithMeta = SubstrateInjectedAccountWithMeta | EvmInjectedAccountWithMeta;

export type ReadonlyAccount = Omit<SubstrateInjectedAccountWithMeta, 'meta' | 'readonly'> & {
  readonly: true;
};

type PolkadotContext = {
  installedWallets: string[];
  injectedExtensions: InjectedExtension[];
  injectedAccounts: InjectedAccountWithMeta[];
  rejectedWallets: string[];
  isInitializingExtensions: boolean;
  connectSnap: () => Promise<InjectedAccountWithMeta>;
  retryConnectWallet: (name: string) => void;
  selectedAccount: InjectedAccountWithMeta | ReadonlyAccount | null;
  selectedSigner: InjectedSigner | null;
  selectAccount: (account: InjectedAccountWithMeta | null) => void;
  setShouldFetchExtensionData: (value: boolean) => void;
  disconnect: () => void;
};

type RequireKeys<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};

type AssertedPolkadotContext = RequireKeys<PolkadotContext, 'selectedAccount'>;

export const MM_SNAP_SOURCE = 'metamask-polkadot-snap';
export const POLKADOT_JS_SOURCE = 'polkadot-js';
export const SUBWALLET_JS_SOURCE = 'subwallet-js';

const initMetamaskSnap = async () => {
  await initPolkadotSnap({
    config: {
      networkName: 'chainflip',
      addressPrefix: 2112,
      unit: {
        symbol: 'FLIP',
        decimals: 18,
      },
      // despite the ws prefix the package expects a http url: https://github.com/ChainSafe/metamask-snap-polkadot/blob/c569a7edcef618ded74e72b729ac76229fe06234/packages/snap/src/polkadot/api.ts#L13
      wsRpcUrl: process.env.NEXT_PUBLIC_STATECHAIN_RPC_HTTP_URL,
    },
  });
};

const EXTENSION_TIMEOUT_MS = 3000;

const getInjectedWeb3 = () =>
  (window as Window & { injectedWeb3?: Record<string, InjectedWindowProvider> }).injectedWeb3 ?? {};

export const withTimeout = <T,>(promise: Promise<T>, fallback: T): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;

  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), EXTENSION_TIMEOUT_MS);
    }),
  ]);
};

// wallet extensions inject their providers into the window before the document finishes loading
const documentReady = () =>
  new Promise<void>((resolve) => {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', () => resolve(), { once: true });
  });

type EnableResult = { extension: EnabledExtension | null; rejected: boolean };

// enable requests stay pending until the user handles the connection popup of the extension.
// the promises are cached to prevent refetches from spamming the extensions with duplicate requests
const enablePromises = new Map<string, Promise<EnableResult>>();

const enableExtension = (name: string, appName: string): Promise<EnableResult> => {
  let promise = enablePromises.get(name);
  if (!promise) {
    promise = (async (): Promise<EnableResult> => {
      const provider = getInjectedWeb3()[name];
      if (provider?.connect) {
        return { extension: await provider.connect(appName), rejected: false };
      }
      if (provider?.enable) {
        const injected = await provider.enable(appName);
        return {
          extension: { name, version: provider.version ?? 'unknown', ...injected },
          rejected: false,
        };
      }
      return { extension: null, rejected: false };
    })().catch(() => ({ extension: null, rejected: true }));
    enablePromises.set(name, promise);
  }
  return promise;
};

// drop the cached request so the next enableExtension call sends a fresh connection request to
// the extension. used to recover from declined requests or requests the extension lost track of
const resetEnableExtension = (name: string) => {
  enablePromises.delete(name);
};

const getExtensionAccounts = async (
  extension: EnabledExtension,
): Promise<InjectedAccountWithMeta[]> => {
  try {
    const accounts = await extension.accounts.get();
    return (
      accounts
        // subwallet includes ethereum accounts if the evm wallet is connected to the page
        // ethereum accounts are only compatible with moonbeam: https://polkadot.js.org/docs/keyring/start/basics/#keypair-types
        .filter((account) => account.type !== 'ethereum')
        .map((account) => ({
          idHex: ss58.toPublicKey(account.address),
          idSs58: reencodeSS58(account.address),
          readonly: false,
          type: 'substrate',
          meta: { name: account.name, source: extension.name },
        }))
    );
  } catch {
    return [];
  }
};

const getExtensionData = async (appName: string) => {
  await documentReady();

  // if the snap is connected we need to initialize it before we can access its accounts.
  // the snap rpc requests are guarded with timeouts because a corrupted metamask provider
  // (e.g. multiple wallet extensions fighting over window.ethereum) leaves them pending forever
  const isSnapConnectedToPage = window.ethereum
    ? await withTimeout(isPolkadotSnapInstalled('npm:@chainsafe/polkadot-snap'), false)
    : false;
  if (isSnapConnectedToPage) {
    await withTimeout(
      initMetamaskSnap().catch(() => false),
      false,
    );
  }

  const installedWallets = Object.keys(getInjectedWeb3());

  // extensions are enabled individually so a single unresponsive wallet (e.g. locked or with a
  // pending connection request) cannot block the detection and accounts of the other wallets
  const enableResults = await Promise.all(
    installedWallets.map((name) => withTimeout(enableExtension(name, appName), null)),
  );
  const injectedExtensions = enableResults
    .map((result) => result?.extension)
    .filter((ext): ext is EnabledExtension => Boolean(ext));
  const rejectedWallets = installedWallets.filter((_, index) => enableResults[index]?.rejected);

  const injectedAccounts = (
    await Promise.all(injectedExtensions.map((ext) => withTimeout(getExtensionAccounts(ext), [])))
  ).flat();

  return { installedWallets, rejectedWallets, injectedExtensions, injectedAccounts };
};

const tryParseAccount = (): ReadonlyAccount | undefined => {
  const accountId = new URLSearchParams(window.location.search).get('accountId');
  if (typeof accountId !== 'string') return undefined;

  try {
    return {
      idHex: ss58.toPublicKey(accountId),
      idSs58: reencodeSS58(accountId),
      readonly: true,
      type: 'substrate',
    };
  } catch {
    return undefined;
  }
};

export const pathsWithoutAccount = [
  '/onboard',
  '/disconnect',
  '/',
  '/v2/portfolio',
  '/v2/portfolio/activity',
];

const Context = createContext<PolkadotContext | null>(null);

type ProviderProps = {
  children: ReactNode;
  polkadotAppName: string;
  storeAccount: InjectedAccountWithMeta | null;
  savePolkadotAccount: PolkadotContext['selectAccount'];
  evmScAccountSupported?: boolean;
};

export const PolkadotProvider = ({
  children,
  polkadotAppName,
  storeAccount,
  savePolkadotAccount,
  evmScAccountSupported,
}: ProviderProps): React.JSX.Element => {
  const [shouldFetchExtensionData, setShouldFetchExtensionData] = useState(false);
  const {
    data: extensionData = {
      installedWallets: [],
      rejectedWallets: [],
      injectedExtensions: [],
      injectedAccounts: [],
    },
    isPending: isInitializingExtensions,
    refetch: refetchExtensionData,
  } = useQuery({
    queryKey: ['polkadotContext'],
    queryFn: () => getExtensionData(polkadotAppName),
    refetchInterval: (query) => {
      if (shouldFetchExtensionData) return 1000;
      // retry in the background until the stored account's extension responds with a signer
      if (
        storeAccount?.type === 'substrate' &&
        !query.state.data?.injectedExtensions.some((ext) => ext.name === storeAccount.meta.source)
      ) {
        return 5000;
      }
      return false;
    },
    enabled: shouldFetchExtensionData || storeAccount !== null,
  });
  const router = useRouter();
  const [readonlyAccount, setReadonlyAccount] = useState<ReadonlyAccount | undefined>(
    tryParseAccount,
  );
  const isEvmAccountInitialized = useIsEvmAccountInitialized();
  const { disconnect: disconnectEvm } = useDisconnectEvm();

  useEffect(() => {
    const acct = tryParseAccount();
    if (acct) setReadonlyAccount(acct);
  }, [window.location.search]);

  // keep readonly account id in query to allow for refreshing page
  useEffect(() => {
    if (router.isReady && !router.query.accountId && readonlyAccount) {
      router.replace({
        pathname: router.pathname,
        query: { ...router.query, accountId: readonlyAccount.idSs58 },
      });
    }
  }, [router.isReady, router.query.accountId, readonlyAccount]);

  const selectedAccount = readonlyAccount ?? storeAccount;
  const selectedSigner = useMemo(() => {
    const extension = !selectedAccount?.readonly
      ? extensionData.injectedExtensions.find((e) => e.name === selectedAccount?.meta.source)
      : undefined;

    return extension?.signer ?? null;
  }, [selectedAccount, extensionData]);

  const selectAccount = useCallback(
    (account: InjectedAccountWithMeta | null) => {
      if (readonlyAccount) setReadonlyAccount(undefined);
      savePolkadotAccount(account);
    },
    [readonlyAccount, savePolkadotAccount],
  );

  const connectSnap = useCallback(async () => {
    await initMetamaskSnap();
    // enabling the snap extension triggers the metamask connect popup if the user did not connect
    // yet. the user clicked to connect, so we always send a fresh request and wait for the user
    // to handle the popup without a timeout
    resetEnableExtension(MM_SNAP_SOURCE);
    const { extension: snapExtension } = await enableExtension(MM_SNAP_SOURCE, polkadotAppName);
    assert(snapExtension, 'failed to enable the metamask snap');
    const [snapAccount] = await getExtensionAccounts(snapExtension);
    assert(snapAccount, 'no metamask snap account injected');
    await refetchExtensionData();

    return snapAccount;
  }, [polkadotAppName, refetchExtensionData]);

  const retryConnectWallet = useCallback(
    (name: string) => {
      resetEnableExtension(name);
      refetchExtensionData().catch(() => undefined);
    },
    [refetchExtensionData],
  );

  const disconnect = useCallback(() => {
    if (evmScAccountSupported && selectedAccount?.type === 'evm') disconnectEvm();
    selectAccount(null);
  }, [selectedAccount]);

  useEffect(() => {
    if (!evmScAccountSupported) return;
    // wait until the evm connector has either connected the existing account
    // or no account is connected
    if (!isEvmAccountInitialized) return;

    // if there is no selected account, or the user selected account gets
    // disconnected, disconnect the evm wallet in anticipation of the user
    // selecting the evm option to connect to the app
    if (selectedAccount === null) disconnectEvm();
  }, [isEvmAccountInitialized, selectedAccount]);

  const value = useMemo(
    () => ({
      ...extensionData,
      isInitializingExtensions,
      connectSnap,
      retryConnectWallet,
      selectedAccount,
      selectedSigner,
      selectAccount,
      setShouldFetchExtensionData,
      disconnect,
    }),
    [
      extensionData,
      isInitializingExtensions,
      connectSnap,
      retryConnectWallet,
      selectedAccount,
      selectedSigner,
      selectAccount,
      disconnect,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export function usePolkadot(): PolkadotContext;
export function usePolkadot(assertAccountSelected: true): AssertedPolkadotContext;
export function usePolkadot(
  assertAccountSelected?: true,
): PolkadotContext | AssertedPolkadotContext {
  const context = useContext(Context);
  if (context === null) {
    throw new Error('usePolkadot must be used within a PolkadotProvider');
  }

  if (assertAccountSelected) {
    if (context.selectedAccount === null) {
      throw new Error('No Polkadot account selected');
    }
  }

  return context;
}
